import React, { useState } from 'react';
import {
  Music,
  Loader2,
  AlertCircle,
  Save,
  Check,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  Youtube,
  FileText,
  Search,
  AlertTriangle,
  CheckCircle,
  Info,
  ShieldAlert,
} from 'lucide-react';
import { analyzeWorshipSong } from '../../lib/openai';
import type { WorshipSongAnalysisJSON, SongCategoryScore, WorshipSongConcernFlag } from '../../lib/openai';
import { useWorshipSongAnalysis } from '../../hooks/useWorshipSongAnalysis';
import type { WorshipSongAnalysisRecord } from '../../hooks/useWorshipSongAnalysis';
import { ConfirmationModal } from '../ConfirmationModal';
import { SaveNotification } from '../SaveNotification';

type InputMode = 'lyrics' | 'youtube' | 'title';

const CATEGORY_LABELS: Record<string, string> = {
  biblicalFaithfulness: 'Biblical Faithfulness',
  godCenteredness: 'God-Centeredness',
  doctrinalClarity: 'Doctrinal Clarity',
  trinitarianClarity: 'Trinitarian Clarity',
  gospelSubstance: 'Gospel Substance',
  congregationalSingability: 'Congregational Singability',
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  biblicalFaithfulness: 'Does the song accurately reflect Scripture?',
  godCenteredness: 'Is it primarily about God or the singer\'s feelings?',
  doctrinalClarity: 'Does it clearly communicate truth?',
  trinitarianClarity: 'Does it distinguish Father, Son, and Spirit faithfully?',
  gospelSubstance: 'Does it contain cross, grace, repentance, redemption?',
  congregationalSingability: 'Is it suited for gathered worship?',
};

function scoreColor(score: number) {
  if (score >= 80) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' };
  if (score >= 60) return { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
  return { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
}

function recommendationConfig(rec: WorshipSongAnalysisJSON['recommendation']) {
  switch (rec) {
    case 'Approved':
      return { icon: CheckCircle, className: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Approved for Worship' };
    case 'Use with Caution':
      return { icon: AlertTriangle, className: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Use with Caution' };
    case 'Not Recommended':
      return { icon: ShieldAlert, className: 'bg-red-100 text-red-800 border-red-300', label: 'Not Recommended' };
  }
}

function severityColor(severity: WorshipSongConcernFlag['severity']) {
  switch (severity) {
    case 'high': return 'bg-red-100 text-red-800 border-red-300';
    case 'medium': return 'bg-amber-100 text-amber-800 border-amber-300';
    case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
  }
}

function CategoryCard({ label, description, data }: { label: string; description: string; data: SongCategoryScore }) {
  const [expanded, setExpanded] = useState(false);
  const colors = scoreColor(data.score);
  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden`}>
      <div
        className="p-4 cursor-pointer hover:bg-white/40 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-midnight">{label}</span>
              <span className={`text-sm font-bold ${colors.text} ml-3 shrink-0`}>{data.score}/100</span>
            </div>
            <div className="w-full bg-white/60 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full ${colors.bar} transition-all duration-500`}
                style={{ width: `${data.score}%` }}
              />
            </div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate shrink-0" />}
        </div>
        {!expanded && <p className="text-xs text-slate mt-1.5">{description}</p>}
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/40 space-y-3">
          <p className="text-sm text-midnight pt-3">{data.summary}</p>
          {data.evidence.length > 0 && (
            <div className="space-y-1.5">
              {data.evidence.map((e, i) => (
                <div key={i} className="flex items-start gap-2 bg-white/60 rounded-lg p-2">
                  <span className="text-xs text-slate font-medium shrink-0 mt-0.5">→</span>
                  <span className="text-xs text-midnight italic">"{e}"</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnalysisResult({
  data,
  onSave,
  onReset,
  isSaved,
  saving,
}: {
  data: WorshipSongAnalysisJSON;
  onSave: () => void;
  onReset: () => void;
  isSaved: boolean;
  saving: boolean;
}) {
  const rec = recommendationConfig(data.recommendation);
  const RecIcon = rec.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-sand/20 shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-midnight">{data.songTitle}</h2>
            {data.artistOrBand && <p className="text-sm text-slate mt-0.5">{data.artistOrBand}</p>}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={onReset}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sand/20 text-slate hover:bg-sand/40 transition-colors text-sm"
            >
              <RotateCcw className="h-4 w-4" />New Analysis
            </button>
            <button
              onClick={onSave}
              disabled={isSaved || saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-electric text-white hover:bg-electric/90 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isSaved ? <><Check className="h-4 w-4" />Saved</> : <><Save className="h-4 w-4" />Save</>}
            </button>
          </div>
        </div>

        {/* Overall score + recommendation */}
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="relative h-16 w-16 shrink-0">
              <svg className="h-16 w-16 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15.9" fill="none"
                  stroke={data.overallScore >= 80 ? '#10b981' : data.overallScore >= 60 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="3"
                  strokeDasharray={`${data.overallScore} ${100 - data.overallScore}`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-midnight">
                {data.overallScore}
              </span>
            </div>
            <div>
              <p className="text-xs text-slate uppercase tracking-widest font-medium">Overall Score</p>
              <p className="text-lg font-bold text-midnight">{data.overallScore}/100</p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium ${rec.className}`}>
            <RecIcon className="h-4 w-4" />
            {rec.label}
          </span>
        </div>

        <p className="mt-4 text-sm text-midnight leading-relaxed">{data.summary}</p>
      </div>

      {/* Category scores */}
      <div>
        <h3 className="text-sm font-semibold text-midnight mb-3 flex items-center gap-2">
          <Music className="h-4 w-4 text-electric" />
          Category Breakdown
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(data.categories).map(([key, val]) => (
            <CategoryCard
              key={key}
              label={CATEGORY_LABELS[key] || key}
              description={CATEGORY_DESCRIPTIONS[key] || ''}
              data={val}
            />
          ))}
        </div>
      </div>

      {/* Concern flags */}
      {data.concernFlags.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-midnight mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Concern Flags
          </h3>
          <div className="space-y-2">
            {data.concernFlags.map((flag, i) => (
              <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${severityColor(flag.severity)}`}>
                <div className="shrink-0">
                  <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${flag.severity === 'high' ? 'bg-red-200' : flag.severity === 'medium' ? 'bg-amber-200' : 'bg-blue-200'}`}>
                    {flag.severity}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold">{flag.flag}</p>
                  {flag.evidence && <p className="text-xs mt-0.5 opacity-80">{flag.evidence}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Positive highlights */}
      {data.positiveHighlights.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-midnight mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            Positive Highlights
          </h3>
          <div className="space-y-2">
            {data.positiveHighlights.map((h, i) => (
              <div key={i} className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
                <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-sm text-midnight">{h}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="flex items-start gap-2.5 bg-sand/20 border border-sand/40 rounded-lg px-4 py-3">
        <Info className="h-4 w-4 text-slate shrink-0 mt-0.5" />
        <p className="text-xs text-slate">{data.disclaimer}</p>
      </div>
    </div>
  );
}

function HistoryItem({
  record,
  onDelete,
}: {
  record: WorshipSongAnalysisRecord;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const rec = recommendationConfig(record.content.recommendation);
  const RecIcon = rec.icon;
  return (
    <div className="bg-white rounded-xl border border-sand/20 overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand/10 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="shrink-0 p-2 bg-purple-100 rounded-lg">
            <Music className="h-4 w-4 text-purple-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-midnight truncate">{record.content.songTitle}</p>
            <p className="text-xs text-slate">{record.content.artistOrBand} · {new Date(record.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${rec.className}`}>
            <RecIcon className="h-3 w-3" />
            {rec.label}
          </span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(record.id); }}
            className="p-2 text-slate hover:text-red-600 rounded-lg hover:bg-red-50 transition-all"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate" /> : <ChevronDown className="h-4 w-4 text-slate" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-sand/20 p-5">
          <AnalysisResult
            data={record.content}
            onSave={() => {}}
            onReset={() => {}}
            isSaved={true}
            saving={false}
          />
        </div>
      )}
    </div>
  );
}

export function WorshipSongAnalysis() {
  const [inputMode, setInputMode] = useState<InputMode>('lyrics');
  const [lyrics, setLyrics] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [songTitle, setSongTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WorshipSongAnalysisJSON | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveNotification, setSaveNotification] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { analyses, saveAnalysis, deleteAnalysis } = useWorshipSongAnalysis();

  const handleAnalyze = async () => {
    setError(null);
    setAnalyzing(true);
    setResult(null);
    setSaved(false);
    try {
      const res = await analyzeWorshipSong({
        lyrics: inputMode === 'lyrics' ? lyrics : undefined,
        youtubeUrl: inputMode === 'youtube' ? youtubeUrl : undefined,
        songTitle: (inputMode === 'title' || inputMode === 'lyrics') ? songTitle || undefined : undefined,
        artist: artist || undefined,
      });
      if (res.success) {
        setResult(res.content as WorshipSongAnalysisJSON);
      } else {
        setError(res.error || 'Analysis failed. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!result || saved) return;
    setSaving(true);
    try {
      await saveAnalysis({
        title: `${result.songTitle}${result.artistOrBand ? ` — ${result.artistOrBand}` : ''}`,
        content: result,
      });
      setSaved(true);
      setSaveNotification(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAnalysis(deleteTarget);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const canAnalyze = !analyzing && (
    (inputMode === 'lyrics' && lyrics.trim().length > 20) ||
    (inputMode === 'youtube' && youtubeUrl.trim()) ||
    (inputMode === 'title' && songTitle.trim())
  );

  const INPUT_TABS: { id: InputMode; label: string; icon: React.ElementType }[] = [
    { id: 'lyrics', label: 'Paste Lyrics', icon: FileText },
    { id: 'youtube', label: 'YouTube URL', icon: Youtube },
    { id: 'title', label: 'Song Title', icon: Search },
  ];

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-midnight">Worship Song Analysis</h2>
          <p className="text-sm text-slate mt-0.5">Evaluate a song's theological faithfulness for congregational worship</p>
        </div>
      </div>

      {/* Input panel */}
      {!result && (
        <div className="bg-white border border-sand/30 rounded-xl shadow-sm p-5 mb-6">
          {/* Mode tabs */}
          <div className="flex gap-1 bg-sand/20 rounded-lg p-1 mb-5 w-fit">
            {INPUT_TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setInputMode(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    inputMode === tab.id
                      ? 'bg-white text-midnight shadow-sm'
                      : 'text-slate hover:text-midnight'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Optional title/artist fields on lyrics tab */}
          {inputMode === 'lyrics' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <input
                type="text"
                placeholder="Song title (optional)"
                value={songTitle}
                onChange={e => setSongTitle(e.target.value)}
                className="px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Artist / band (optional)"
                value={artist}
                onChange={e => setArtist(e.target.value)}
                className="px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
              />
            </div>
          )}

          {inputMode === 'lyrics' && (
            <textarea
              value={lyrics}
              onChange={e => setLyrics(e.target.value)}
              placeholder="Paste the song lyrics here…"
              rows={8}
              className="w-full px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none resize-none"
            />
          )}

          {inputMode === 'youtube' && (
            <div className="space-y-3">
              <input
                type="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={e => setYoutubeUrl(e.target.value)}
                className="w-full px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
              />
              <p className="text-xs text-slate">The AI will analyze the song using the video's transcript/captions and its training knowledge of the song.</p>
            </div>
          )}

          {inputMode === 'title' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Song title (required)"
                  value={songTitle}
                  onChange={e => setSongTitle(e.target.value)}
                  className="px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="Artist / band (optional)"
                  value={artist}
                  onChange={e => setArtist(e.target.value)}
                  className="px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
                />
              </div>
              <p className="text-xs text-slate">The AI will analyze the song based on its training knowledge. Well-known worship songs will have more detailed analysis than obscure titles.</p>
            </div>
          )}

          {error && (
            <div className="mt-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <div className="mt-4">
            <button
              onClick={handleAnalyze}
              disabled={!canAnalyze}
              className="flex items-center gap-2 px-5 py-2.5 bg-electric text-white text-sm font-medium rounded-lg hover:bg-electric/90 disabled:opacity-50 transition-colors"
            >
              {analyzing ? (
                <><Loader2 className="h-4 w-4 animate-spin" />Analyzing…</>
              ) : (
                <><Music className="h-4 w-4" />Analyze Song</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {analyzing && (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-electric" />
          <p className="text-sm text-slate">Evaluating theological content…</p>
        </div>
      )}

      {/* Result */}
      {result && !analyzing && (
        <AnalysisResult
          data={result}
          onSave={handleSave}
          onReset={() => { setResult(null); setSaved(false); setError(null); }}
          isSaved={saved}
          saving={saving}
        />
      )}

      {/* History */}
      {!result && !analyzing && analyses.length > 0 && (
        <div className="mt-2">
          <div className="flex items-center gap-2 mb-3">
            <Music className="h-4 w-4 text-purple-600" />
            <h3 className="text-sm font-semibold text-midnight">Saved Analyses</h3>
            <span className="text-xs text-slate bg-sand/40 px-2 py-0.5 rounded-full">{analyses.length}</span>
          </div>
          <div className="space-y-3">
            {analyses.map(a => (
              <HistoryItem key={a.id} record={a} onDelete={setDeleteTarget} />
            ))}
          </div>
        </div>
      )}

      {!result && !analyzing && analyses.length === 0 && (
        <div className="text-center py-14 bg-white rounded-xl border border-sand/20">
          <Music className="h-12 w-12 text-slate/30 mx-auto mb-3" />
          <p className="text-slate text-sm max-w-sm mx-auto">Paste lyrics, enter a YouTube URL, or type a song title to evaluate a worship song's theological faithfulness from a conservative evangelical perspective.</p>
        </div>
      )}

      <SaveNotification
        show={saveNotification}
        onHide={() => setSaveNotification(false)}
        message="Song analysis saved"
        type="success"
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Analysis"
        message="Are you sure you want to permanently delete this song analysis?"
        confirmText="Delete"
        icon={<Trash2 className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
}
